import React from 'react';
import { Box, Stack } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';

import { book } from '../../routers/books';
import { useGetJobsAccountHomeQuery } from '../../services/serviceJobsAccount';
import {
  getJobsAccountRole,
  getJobsAccountRolePath,
  hasBothJobsAccountRoles,
} from '../../utils/jobsAccountRole';
import { CPG_USER_ACCOUNT_URL } from '../CpgChrome/cpgSites';

export const DashboardNavigation = () => {
  const { pathname } = useLocation();
  const { data } = useGetJobsAccountHomeQuery();
  const role = getJobsAccountRole(data, pathname);
  const hasBothRoles = hasBothJobsAccountRoles(data);
  const isEmployer = role === 'employer';
  const alternateRole = isEmployer ? 'jobSeeker' : 'employer';
  const roleHomePath = getJobsAccountRolePath(role);

  let navigation: Array<{ label: string; to: string; active: boolean }>;
  if (!data) {
    navigation = [{
      label: 'Overview',
      to: book.jobsAccount,
      active: pathname === book.jobsAccount,
    }];
  } else if (isEmployer) {
    navigation = [
      {
        label: 'Overview',
        to: roleHomePath,
        active: pathname === book.jobsAccount || pathname === roleHomePath,
      },
      {
        label: 'Applicants',
        to: book.jobsNewApplicants,
        active: pathname.indexOf(book.jobsAllApplicants) === 0,
      },
    ];
    if (data.hasListings) {
      navigation.splice(1, 0, {
        label: 'Job Listings',
        to: book.jobsListings,
        active: pathname === book.jobsListings,
      });
      navigation.push({
        label: 'Prepaid Packages',
        to: book.jobsPrepaid,
        active: pathname.indexOf(book.jobsPrepaid) === 0,
      });
    }
    navigation.push({
      label: data.canAccessResumeDb ? 'Resume Database' : 'Resume Database · Featured',
      to: book.resumeDatabase,
      active: pathname.indexOf(book.resumeDatabase) === 0,
    });
  } else {
    navigation = [
      {
        label: 'Overview',
        to: roleHomePath,
        active: pathname === book.jobsAccount || pathname === roleHomePath,
      },
      {
        label: 'My Applications',
        to: book.myApplications,
        active: pathname === book.myApplications,
      },
      {
        label: 'Saved Jobs',
        to: book.savedJobs,
        active: pathname === book.savedJobs,
      },
      {
        label: 'My Resume',
        to: book.myResume,
        active: pathname.indexOf(book.myResume) === 0,
      },
    ];
  }

  const navigationStyles = {
    alignItems: 'center',
    color: 'var(--ink-3)',
    display: 'flex',
    fontSize: '0.75rem',
    fontWeight: 700,
    minHeight: 56,
    position: 'relative',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      color: 'var(--ink)',
    },
  } as const;

  return (
    <Stack
      direction="row"
      spacing={{ xs: 2.5, sm: 4 }}
      component="nav"
      aria-label="Jobs account"
      sx={{ overflowX: 'auto' }}
    >
      {navigation.map((item) => (
        <Box
          key={item.label}
          component={Link}
          to={item.to}
          aria-current={item.active ? 'page' : undefined}
          sx={{
            ...navigationStyles,
            color: item.active ? 'var(--ink)' : 'var(--ink-3)',
            '&::after': item.active ? {
              backgroundColor: 'var(--accent)',
              bottom: 0,
              content: '""',
              height: 3,
              left: 0,
              position: 'absolute',
              right: 0,
            } : undefined,
          }}
        >
          {item.label}
        </Box>
      ))}
      {hasBothRoles ? (
        <Box
          component={Link}
          to={getJobsAccountRolePath(alternateRole)}
          sx={{
            ...navigationStyles,
            color: 'var(--accent-text)',
          }}
        >
          {isEmployer ? 'Job seeker view' : 'Employer view'}
        </Box>
      ) : null}
      <Box
        component="a"
        href={CPG_USER_ACCOUNT_URL}
        target="_blank"
        rel="noopener noreferrer"
        sx={navigationStyles}
      >
        Manage Account ↗
      </Box>
    </Stack>
  );
};

export default DashboardNavigation;
